import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { PatientPaymentControllerApi } from "../../../services";
import { getConfigObject, getXtenantId } from "../../../utils/utils";

/**
 * Patient-level payment balance summary for the Payment History tab.
 *
 * Endpoint: GET /api/master/patient-payment/v2/by-visit (called without a
 * visitId so the backend returns the patient-wide aggregate). The wrapped
 * response carries, under `data`:
 *  - creditBalance   -> total unused payment across the patient (Credit Balance tag)
 *  - previousBalance -> total outstanding balance across the patient (Outstanding Balance tag)
 *  - totalPaymentReceived -> total payments received across the patient (Payment History total)
 *
 * @author Atish Bagate
 */
interface PatientPaymentSummary {
  creditBalance?: number;
  previousBalance?: number;
  totalPaymentReceived?: number;
}

interface PatientPaymentSummaryResponse {
  data?: PatientPaymentSummary;
}

interface Props {
  patientUuid?: string;
  enabled?: boolean;
}

export function useGetPatientPaymentSummary({
  patientUuid,
  enabled = true,
}: Props): UseQueryResult<
  PatientPaymentSummaryResponse | undefined,
  AxiosError
> {
  return useQuery({
    queryKey: ["get-patient-payment-summary", patientUuid],
    refetchOnWindowFocus: false,
    enabled: enabled && !!patientUuid,
    queryFn: async () =>
      new PatientPaymentControllerApi(getConfigObject())
        .getPatientPaymentsByVisit(patientUuid ?? "", undefined, getXtenantId())
        .then((it) => it?.data as PatientPaymentSummaryResponse | undefined)
        .catch(() => {
          return undefined;
        }),
  });
}
